import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ArrowLeft, HelpCircle, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PhoenixWordmark } from "@/components/PhoenixLogo";

/** Public flow — inmate lost their access code. Queues a reset request for facility staff. */
export default function ForgotAccessCodePage() {
    const [inmateNumber, setInmateNumber] = useState("");
    const [dob, setDob] = useState("");
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post("/auth/forgot-access-code", {
                inmate_number: inmateNumber.trim(),
                date_of_birth: dob,
            });
            setMessage(data.message);
        } catch {
            setMessage("If your details match our records, facility staff have been notified and will issue you a new access code.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-phoenix-mesh flex flex-col">
            <header className="max-w-6xl mx-auto w-full px-6 py-6 flex items-center justify-between">
                <PhoenixWordmark />
                <Link
                    to="/claim-access"
                    className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-phoenix-navy transition-colors"
                >
                    <ArrowLeft className="w-4 h-4" /> Back to sign in
                </Link>
            </header>

            <div className="flex-1 flex items-center justify-center px-6 py-10">
                <div className="w-full max-w-md">
                    <div className="text-center mb-7 animate-fade-up">
                        <div className="inline-flex items-center justify-center w-14 h-14 bg-phoenix-navy text-white rounded-2xl mb-4 shadow-phoenix-card">
                            <HelpCircle className="w-6 h-6" />
                        </div>
                        <h1 className="font-display font-bold text-3xl text-phoenix-navy mb-2">
                            Lost your access code?
                        </h1>
                        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                            Enter your inmate number and date of birth. If they match our records,
                            facility staff will be notified to issue you a new code.
                        </p>
                    </div>

                    {message ? (
                        <div
                            data-testid="forgot-code-success"
                            className="bg-white p-7 rounded-3xl shadow-phoenix-card border border-border/60 text-center space-y-4 animate-fade-up"
                        >
                            <CheckCircle2 className="w-10 h-10 text-success mx-auto" />
                            <p className="text-sm text-phoenix-navy leading-relaxed">{message}</p>
                            <p className="text-xs text-muted-foreground">
                                A staff member will hand you your new code. There is nothing else you need to do.
                            </p>
                            <Link
                                to="/claim-access"
                                data-testid="forgot-code-back-link"
                                className="inline-block w-full py-3 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all text-sm"
                            >
                                Back to sign in
                            </Link>
                        </div>
                    ) : (
                        <form
                            onSubmit={handleSubmit}
                            data-testid="forgot-code-form"
                            className="bg-white p-7 rounded-3xl shadow-phoenix-card border border-border/60 space-y-5 animate-fade-up"
                        >
                            <div>
                                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                    Inmate Number
                                </label>
                                <input
                                    type="text"
                                    value={inmateNumber}
                                    onChange={(e) => setInmateNumber(e.target.value)}
                                    required
                                    data-testid="forgot-code-inmate-number"
                                    className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                    placeholder="BP-1001"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-semibold text-phoenix-navy uppercase tracking-wider mb-2">
                                    Date of Birth
                                </label>
                                <input
                                    type="date"
                                    value={dob}
                                    onChange={(e) => setDob(e.target.value)}
                                    required
                                    data-testid="forgot-code-dob"
                                    className="w-full px-4 py-3 border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-phoenix-navy/30 focus:border-phoenix-navy transition-all"
                                />
                            </div>
                            <button
                                type="submit"
                                disabled={loading}
                                data-testid="forgot-code-submit"
                                className="w-full py-3.5 bg-phoenix-navy text-white font-semibold rounded-xl hover:bg-phoenix-navy-2 transition-all disabled:opacity-60 flex items-center justify-center gap-2"
                            >
                                {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                                Request new code
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
}
